import React, { useEffect, useState } from "react";
import styles from "../css/css_pages/clientProfile.module.css";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import config from "../../config";
import { FiImage } from "react-icons/fi";
import profileImage from "../assets/profile_picture_image.png";

const ClientProfile = () => {
  const { id } = useParams();
  const [token, setToken] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedFile, setSelectedFile] = useState({ name: "", type: "" });
  const [data, setData] = useState([]);
  const [userProfile, setUserProfile] = useState({
    profilePicture: "",
    full_name: "",
    type: "",
    emp_no: "",
    phone_number: "",
  });
  const DEFAULT_PROFILE_IMAGE = profileImage; // Default profile image path
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    const authToken = Cookies.get("authToken");
    console.log("Auth token from cookies:", authToken); // Log token
    setToken(authToken);
  }, []);

  useEffect(() => {
    if (token) {
      console.log("Token available, fetching client data..."); // Log before fetch
      fetchClient(id);
      fetchUserProfile();
    }
  }, [token]);

  const fetchClient = async (clientId) => {
    const token = Cookies.get("authToken");
    if (!token) {
      console.error("No token available");
      return;
    }

    try {
      console.log("Starting to fetch all client data..."); // Log start of fetch
      const [
        clientDataRes,
        schedulesRes,
        ticketsRes,
        feedbackRes,
        invoicesRes,
      ] = await Promise.all([
        fetch(
          `${config.API_URL}/tso-online/get-details-by-client?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        fetch(
          `${config.API_URL}/tso-online/get-schedules-by-client?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        fetch(
          `${config.API_URL}/tso-online/get-tickets-by-client?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        fetch(
          `${config.API_URL}/tso-online/get-feedback-schedules-by-client?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        fetch(
          `${config.API_URL}/tso-online/list-invoices?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
      ]);

      console.log("All fetch requests completed, checking responses..."); // Log after fetch

      if (
        !clientDataRes.ok ||
        !schedulesRes.ok ||
        !ticketsRes.ok ||
        !feedbackRes.ok ||
        !invoicesRes.ok
      ) {
        throw new Error("Failed to fetch client data");
      }

      const [
        clientData,
        schedulesData,
        ticketsData,
        feedbackData,
        invoiceData,
      ] = await Promise.all([
        clientDataRes.json(),
        schedulesRes.json(),
        ticketsRes.json(),
        feedbackRes.json(),
        invoicesRes.json(),
      ]);

      console.log("Client data:", clientData); // Log client data
      console.log("Schedules data:", schedulesData); // Log schedules data
      console.log("Tickets data (raw):", ticketsData); // Log raw tickets data
      console.log("Feedback data:", feedbackData); // Log feedback data
      console.log("Invoice data:", invoiceData); // Log invoice data

      // Filter out duplicate tickets
      const uniqueTickets = Array.from(
        new Set(ticketsData.map((ticket) => ticket.ticket_no))
      ).map((ticketNo) =>
        ticketsData.find((ticket) => ticket.ticket_no === ticketNo)
      );

      console.log("Filtered unique tickets:", uniqueTickets); // Log filtered tickets

      setData(clientData || []);
      setSchedules(schedulesData || []);
      setTickets(uniqueTickets || []); // Use filtered tickets
      setFeedback(feedbackData || []);
      setInvoices(invoiceData || []);
    } catch (error) {
      console.error("Error fetching client data:", error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found.");

      const profilePictureResponse = await fetch(
        `${config.API_URL}/user/profile-image-by-client?client_id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let profilePictureUrl = DEFAULT_PROFILE_IMAGE;
      if (profilePictureResponse.ok) {
        const profilePictureBlob = await profilePictureResponse.blob();
        profilePictureUrl = URL.createObjectURL(profilePictureBlob);
      }

      setUserProfile({ profilePicture: profilePictureUrl });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserProfile({ profilePicture: DEFAULT_PROFILE_IMAGE });
    }
  };

  const openResource = async (resource, issue) => {
    console.log("Opening resource:", resource, issue); // Debugging
    setSelectedSchedule(issue);
    const url = `${config.API_URL}/client/view-ticket-image?ticket_id=${issue.ticket_id}&file_name=${resource}`;
    const token = Cookies.get("authToken");
    if (!token) throw new Error("Authentication token not found.");

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Fetch response:", response); // Debugging
      if (response.ok) {
        const blob = await response.blob();
        console.log("Blob data:", blob); // Debugging
        const fileType = blob.type;
        console.log("File type:", fileType); // Debugging
        setSelectedFile({ name: resource, type: fileType });
        if (fileType.startsWith("image/")) {
          setImageSrc(URL.createObjectURL(blob));
        } else {
          window.open(URL.createObjectURL(blob), "_blank");
        }
      } else {
        console.error("Failed to fetch the resource:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching the resource:", error);
    }
  };

  const closeImageModal = () => {
    setImageSrc(null);
    setSelectedSchedule(null);
    setSelectedFile({ name: "", type: "" });
  };

  const calculateTotalOutstanding = () => {
    if (!Array.isArray(invoices)) {
      return 0;
    }
    return invoices.reduce((total, invoice) => total + invoice.balance, 0);
  };

  // Log current state of tickets and schedules whenever they change
  useEffect(() => {
    console.log("Current tickets state:", tickets);
  }, [tickets]);

  useEffect(() => {
    console.log("Current schedules state:", schedules);
  }, [schedules]);

  return (
    <div className={styles.clientProfilePage}>
      <h1 className={styles.pageTitle}>Client Profile</h1>
      <div className={styles.clientProfileContent}>
        <div className={styles.clientInfo}>
          <div className={styles.clientHeader}>
            <img
              src={userProfile.profilePicture || DEFAULT_PROFILE_IMAGE}
              alt="Profile"
              onError={(e) => (e.target.src = DEFAULT_PROFILE_IMAGE)}
              className={styles.profilePicture}
            />
            <div>
              <h2 className={styles.clientName}>
                <b>Client:</b> {data?.customer_name}
              </h2>
              <h2 className={styles.clientName}>
                <b>Customer ID:</b> {data?.customer_id}
              </h2>
              <h2 className={styles.clientName}>
                <b>Assigned TSO Officer:</b> {data?.tso_name}
              </h2>
              <h2 className={styles.clientName}>
                <b>Employee No:</b> {data?.tso_id}
              </h2>
            </div>
          </div>

          <div className={styles.clientDetails}>
            <h1 className={styles.sectionTitle}>Schedules</h1>
            {schedules.length > 0 ? (
              schedules.map((schedule, index) => {
                const feedbackItem = feedback.find(
                  (fb) => fb.schedule_no === schedule.schedule_no
                );
                return (
                  <div key={index} className={styles.scheduleItem}>
                    <p>
                      <strong>Description:</strong>{" "}
                      {schedule.schedule_description}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {new Date(schedule.scheduled_date).toLocaleString()}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {schedule.schedule_status
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </p>
                    <p>
                      <strong>Feedback:</strong>{" "}
                      {feedbackItem
                        ? ["Bad", "Average", "Good", "Best"][
                            feedbackItem.checklist_status - 1
                          ]
                        : "No feedback"}
                    </p>
                    {schedule.img_file && (
                      <div className={styles.imageContainer}>
                        {schedule.img_file.split(",").map((resource, idx) => (
                          <button
                            key={idx}
                            onClick={() => openResource(resource, schedule)}
                            className={styles.imageButton}
                          >
                            <FiImage size={20} color="#1C3663" />
                          </button>
                        ))}
                      </div>
                    )}
                    <hr className={styles.divider} />
                  </div>
                );
              })
            ) : (
              <p>No schedules available</p>
            )}

            <h1 className={styles.sectionTitle}>Tickets</h1>
            {tickets.length > 0 ? (
              tickets.map((ticket, index) => (
                <div key={index} className={styles.ticketItem}>
                  <p>
                    <strong>Ticket No:</strong> {ticket.ticket_no}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {ticket.ticket_status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                  <p>
                    <strong>Issue:</strong> {ticket.issue}
                  </p>
                  <p>
                    <strong>Created Date:</strong>{" "}
                    {new Date(ticket.ticket_created_date).toLocaleString()}
                  </p>
                  {ticket.file_name && (
                    <div className={styles.imageContainer}>
                      {ticket.file_name.split(",").map((resource, idx) => (
                        <button
                          key={idx}
                          onClick={() => openResource(resource, ticket)}
                          className={styles.imageButton}
                        >
                          <FiImage size={20} color="#1C3663" />
                        </button>
                      ))}
                    </div>
                  )}
                  <hr className={styles.divider} />
                </div>
              ))
            ) : (
              <p>No tickets available</p>
            )}
          </div>
        </div>

        <div className={styles.clientSideSection}>
          <div className={styles.clientFeedback}>
            <h3>Client Schedule Feedback</h3>
            <table>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Feedback</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(feedback) &&
                  feedback
                    .filter((item) => item.scheduled_date)
                    .sort(
                      (a, b) =>
                        new Date(b.scheduled_date) - new Date(a.scheduled_date)
                    )
                    .slice(0, 5)
                    .map((item, index) => (
                      <tr key={index}>
                        <td>{item.schedule_no}</td>
                        <td>
                          {new Date(item.scheduled_date).toLocaleDateString()}
                        </td>
                        <td>{item.schedule_description}</td>
                        <td>
                          {["Bad", "Average", "Good", "Best"][
                            item.checklist_status - 1
                          ] || "No Feedback"}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          <div className={styles.outstandingAmounts}>
            <h3>Outstanding Amounts</h3>
            <table>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(invoices) &&
                  invoices
                    .filter((invoice) => invoice.balance > 0) // Only include invoices with balance > 0
                    .map((invoice) => (
                      <tr key={invoice.invoice_no}>
                        <td>{invoice.invoice_no}</td>
                        <td>{new Date(invoice.due_date).toLocaleDateString()}</td>
                        <td>{invoice.invoice_type}</td>
                        <td>{invoice.balance}</td>
                      </tr>
                    ))}
                {/* Add a row for Total Outstanding */}
                <tr className={styles.totalOutstandingRow}>
                  <td colSpan="3" className={styles.totalLabel}>
                    Total Outstanding:
                  </td>
                  <td className={styles.totalAmountCell}>
                    <span className={styles.totalAmountValue}>
                      {calculateTotalOutstanding()}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {imageSrc && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeImageModal}>
              &times;
            </span>
            <h3>File Details</h3>
            <p>Name: {selectedFile.name}</p>
            <p>Type: {selectedFile.type}</p>
            <img src={imageSrc} alt="Selected" style={{ maxWidth: "100%" }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProfile;